// Owner rule 2026-10-15: "SKU ที่ไม่มีชื่อตัวเลือก ถือว่าไม่สมบูรณ์ ห้ามเปิดขาย".
//
// A SKU whose option name (sku_name) is NULL, empty or whitespace-only is
// INCOMPLETE: the storefront hides it and the API refuses to put a product with
// such a SKU on sale. Backend contract (mdf-bo-api feat/block-incomplete-sku):
//
//   GET  /api/v1/bo/catalog/products          every row: incomplete_sku_count <int>
//                                             filter:    ?incomplete_sku=1
//   POST /products, PUT /products/:id         app_enable 1 + a SKU that is on
//   PATCH /products/:id/app-enable {1}        (is_active !== false) with a blank
//                                             name -> 400
//     {"Status":"error","Msg":"กรุณาตั้งชื่อตัวเลือกก่อนเปิดขาย",
//      "code":"sku_name_required","sku_codes":[...]}
//
// #976 refinement (owner 2026-10-16): a product with ONE switched-on SKU needs no
// option name and sells as is (the storefront shows no option picker). The rule
// only bites when MORE THAN ONE SKU is switched on and any of them is nameless;
// then every nameless switched-on SKU must be named (switching a second nameless
// SKU on names both). Only switched-on SKUs (is_active !== false) are counted.
// The BE answers the same: incomplete_sku_count is 0 for a single-SKU product
// and the 400 sku_name_required only fires for the multi-SKU case.
//
// Pure + Vue-free so tests/unit/skuIncomplete.spec.js can pin every rule
// (the pages cannot be mounted in this jest setup).

export const SKU_NAME_REQUIRED_CODE = "sku_name_required";
// i18n key of "กรุณาตั้งชื่อตัวเลือกก่อนเปิดขาย" — shown for the client-side guard
// AND for the server's 400, so the operator reads one sentence for one rule.
export const SKU_NAME_REQUIRED_KEY = "app_catalog.sku_name_required";

/**
 * isBlankOptionName(name) — the same test as the BE's strings.TrimSpace(name) == "".
 * String.prototype.trim strips Unicode white space (NBSP, ideographic space …)
 * just like Go does, so a name made of those is blank on both sides.
 */
export function isBlankOptionName(name) {
  if (name === null || name === undefined) return true;
  return String(name).trim() === "";
}

/**
 * incompleteSkuCount(row) — the list badge number. An API older than the rule
 * does not send the field: that is 0 (no badge), never a guess.
 */
export function incompleteSkuCount(row) {
  const n = Number(row && row.incomplete_sku_count);
  return isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function hasIncompleteSku(row) {
  return incompleteSkuCount(row) > 0;
}

/**
 * blankNameSkuIndexes(skus) — editor rows that break the rule: the SKU is on
 * (is_active !== false — a SKU switched off is never sold), its name is blank,
 * AND more than one SKU is on (#976 — the only switched-on SKU needs no name).
 */
export function blankNameSkuIndexes(skus) {
  if (!Array.isArray(skus)) return [];
  const out = [];
  let on = 0;
  skus.forEach((s, i) => {
    if (!s || s.is_active === false) return;
    on += 1;
    if (isBlankOptionName(s.sku_name)) out.push(i);
  });
  return on > 1 ? out : [];
}

function isOn(v) {
  return v === true || Number(v) === 1;
}

/**
 * optionNameSaveGuard({app_enable, skus}) — the editor's check before POST/PUT.
 * Blocked only when the product is being put ON SALE: saving with the switch
 * off is how the operator keeps a half-finished product (BE allows it too).
 * `indexes` is returned either way so the page can mark the rows.
 */
export function optionNameSaveGuard(form) {
  if (!form) return { blocked: false, indexes: [] };
  const indexes = blankNameSkuIndexes(form.skus);
  return { blocked: isOn(form.app_enable) && indexes.length > 0, indexes };
}

/** isOptionNameRequiredError(err) — the BE's 400 for this rule (`code` first). */
export function isOptionNameRequiredError(err) {
  const res = err && err.response;
  if (!res || Number(res.status) !== 400) return false;
  const data = res.data;
  return !!data && typeof data === "object" && data.code === SKU_NAME_REQUIRED_CODE;
}

/**
 * withIncompleteSkuFilter(params) — the list query. `incomplete_sku=1` only when
 * the filter is on; the key is dropped otherwise so an API without the filter
 * sees exactly the query it saw before.
 */
export function withIncompleteSkuFilter(params) {
  const out = { ...(params || {}) };
  if (isOn(out.incomplete_sku)) out.incomplete_sku = 1;
  else delete out.incomplete_sku;
  return out;
}

/**
 * badgeLines(text) — the badge split before its parenthesis, so each half can
 * be `white-space: nowrap` and the tag wraps only there. In the 148px status
 * column the Thai label otherwise breaks inside "(ไม่มีชื่อตัว / เลือก)"
 * (Thai has no spaces, so the browser picks a dictionary break). A label with
 * no " (" comes back as one line.
 */
export function badgeLines(text) {
  const s = text == null ? "" : String(text);
  const i = s.indexOf(" (");
  if (i <= 0) return [s];
  return [s.slice(0, i), s.slice(i + 1)];
}
